import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";

import { ErrorComponent } from "../../../components/error/error.component";

import { Choice } from "../../../dto/choice";
import { PaginatedResult } from "../../../models/default/paginated-result";
import { Analysis, AnalysisStatus } from "../../../models/general/analysis";

import { URLS } from "../../../services/app-urls";
import { BaseService } from "../../../services/base-service";
import { translate } from "../../../translate/translate";
import { registerActiveAnalysisConnections } from "../../../services/analysis.list.service";
import { useAnalysisProgressMap, STORAGE_STATUS_TO_ENUM } from "../../../hooks/useAnalysisProgress";

const DISPLAYED_COLUMNS_SHOW = [
  "select",
  "id",
  "description",
  "processing",
  "type",
  "creation_date",
  "customer",
  "user",
  "action",
];

const DISPLAYED_COLUMNS = [
  "select",
  "id",
  "description",
  "status",
  "parent_analysis_id",
  "created_at",
  "customer",
  "user_name",
  "action",
];

const SHOW_ACTIONS = ["details", "delete"];

const DATE_COLUMNS = ["created_at"];

const STATUS_DOT_COLORS: Record<string, string> = {
  [AnalysisStatus.WAITING]: "#3949ab",
  [AnalysisStatus.COMPLETED]: "#43ba75",
  [AnalysisStatus.ERROR]: "#f44336",
  [AnalysisStatus.CANCELED]: "#f44336",
  [AnalysisStatus.SAVE_ERROR]: "#ff9800",
};

interface UseAnalysisPageReturn {
  analysis: Analysis | undefined;
  setAnalysis: Dispatch<SetStateAction<Analysis | undefined>>;
  dataSource: Analysis[];
  pagination: PaginatedResult<Analysis> | undefined;
  choices: Choice[];
  rowsPerPage: number;
  page: number;
  searchFilter: string;
  openDeleteModal: boolean;
  openAnalysisProcessingDialog: boolean;
  deletingIds: number[];
  selectedIds: number[];
  confirmDeleteMessage: string;
  isLoading: boolean;


  handleChangePage: (_: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRowClick: (row: Analysis) => void;
  handleCloseDeleteModal: (result: unknown) => void;
  handleCloseAnalysisProcessingDialog: () => void;
  handleOpenAnalysisProcessingModal: (row: Analysis) => void;
  handleOpenDeleteModal: (row: Analysis) => void;
  handleSearchSubmit: (values: any, { setSubmitting }: any) => void;
  handleSearchChange: (event: ChangeEvent<{ value: unknown }>) => void;
  percentage: (row: Analysis) => string;
  toggleSelectOne: (id: number) => void;
  toggleSelectAll: () => void;

  DISPLAYED_COLUMNS_SHOW: readonly string[];
  DISPLAYED_COLUMNS: readonly string[];
  SHOW_ACTIONS: readonly string[];
  DATE_COLUMNS: readonly string[];
  STATUS_DOT_COLORS: Record<string, string>;
}

export const useAnalysisPage = (): UseAnalysisPageReturn => {
  const service = new BaseService<Analysis>(URLS.ANALYSIS);
  const navigate = useNavigate();

  const [analysis, setAnalysis] = useState<Analysis>();
  const [dataSource, setDataSource] = useState<Analysis[]>([]);
  const [pagination, setPagination] = useState<PaginatedResult<Analysis>>();
  const [choices, setChoices] = useState<Choice[]>([]);

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [searchFilter, setSearchFilter] = useState("");

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openAnalysisProcessingDialog, setOpenAnalysisProcessingDialog] = useState<boolean>(false);
  const [deletingIds, setDeletingIds] = useState<number[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const allIds = useMemo(() => dataSource.map(a => a.id), [dataSource]);
  const { progressMap, terminalEpoch } = useAnalysisProgressMap(allIds);

  const search = useCallback(() => {
    setIsLoading(true);
    service.clearParameters();
    if (searchFilter) service.addParameter("search", searchFilter);
    service.addParameter("limit", rowsPerPage);
    service.addParameter("offset", page * rowsPerPage);

    service
      .getAllPaginate()
      .then(response => {
        setPagination(response.data);
        setDataSource(response.data.results);
        registerActiveAnalysisConnections(response.data.results);
      })
      .catch((error: any) => {
        ErrorComponent(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [searchFilter, rowsPerPage, page]);

  const getAnalysisChoices = useCallback(() => {
    service
      .getChoices("status")
      .then((response: any) => {
        setChoices(response?.map((item: any) => item));
      })
      .catch((error: any) => {
        ErrorComponent(error);
      });
  }, []);

  useEffect(() => {
    search();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (terminalEpoch > 0) search();
  }, [terminalEpoch]);

  useEffect(() => {
    getAnalysisChoices();
  }, []);

  const mappedDataSource = useMemo(() => {
    return dataSource.map((data) => {
      const isParent = data.parent_analysis_id === undefined;
      const hasChildren = dataSource.some(
        (child) => child.parent_analysis_id === data.id
      );
      const wsData = progressMap[data.id];
      const liveStatus = wsData ? STORAGE_STATUS_TO_ENUM[wsData.status] : data.status;

      return {
        ...data,
        status: liveStatus,
        original: isParent,
        hasChildren,
        description: data.description || "N/A",
      };
    });
  }, [dataSource, progressMap]);

  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const toggleSelectAll = useCallback(() => {
    const selectableAnalyses = dataSource.filter(
      d => d.status !== AnalysisStatus.WAITING && d.status !== AnalysisStatus.PROCESSING
    );
    if (selectedIds.length === selectableAnalyses.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableAnalyses.map(d => d.id));
    }
  }, [selectedIds, dataSource]);

  const toggleSelectOne = useCallback((id: number) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id],
    );
  }, []);

  const confirmDeleteMessage = useMemo(() => {
    if (selectedIds.length > 1) {
      return translate.t("confirm_delete_itens");
    }
    return translate.t("confirm_delete");
  }, [selectedIds]);


  const handleRowClick = useCallback((row: Analysis) => {
    if (!row?.status) return;

    const processingStatuses = [
      AnalysisStatus.PROCESSING,
      AnalysisStatus.WAITING,
    ];
    const errorStatuses = [AnalysisStatus.ERROR, AnalysisStatus.CANCELED];

    if (errorStatuses.includes(row.status)) return;

    const path = processingStatuses.includes(row.status)
      ? `/general/analysis/${row.id}/processing`
      : `/general/analysis/${row.id}/gds-calculation`;

    navigate(path);
  }, [navigate]);

  const handleOpenDeleteModal = useCallback((row: Analysis) => {
    setAnalysis(row);
    setOpenDeleteModal(true);
  }, []);

  const handleCloseDeleteModal = useCallback(
    async (result: unknown) => {
      if (typeof result === "boolean" && result) {
        const idsToDelete =
          selectedIds.length > 0 ? selectedIds : analysis ? [analysis.id] : [];

        if (idsToDelete.length === 0) {
          setOpenDeleteModal(false);
          return;
        }

        try {

          await service.postFromListRoute({ "ids": idsToDelete, }, "bulk-delete");

          toast.success(
            idsToDelete.length > 1
              ? translate.t("all_successfully_deleted")
              : translate.t("successfully_deleted")
          );

          search();
        } catch (error: any) {
          ErrorComponent(error as any);
        } finally {
          setDeletingIds([]);
          setSelectedIds([]);
          setAnalysis(undefined);
          setOpenDeleteModal(false);
        }
      } else {
        setAnalysis(undefined);
        setOpenDeleteModal(false);
      }
    },
    [selectedIds, analysis, service, search]
  );

  const handleOpenAnalysisProcessingModal = useCallback((row: Analysis) => {
    setAnalysis(row);
    setOpenAnalysisProcessingDialog(true);
  }, []);

  const handleCloseAnalysisProcessingDialog = useCallback(() => {
    setAnalysis(undefined);
    setOpenAnalysisProcessingDialog(false);
    search();
  }, [search]);

  const percentage = useCallback((row: Analysis) => {
    const data = progressMap[row.id];
    return data?.progress ? `${data.progress}%` : "0%";
  }, [progressMap]);

  const handleSearchSubmit = useCallback((values: any, { setSubmitting }: any) => {
    setTimeout(() => {
      if (page === 0) {
        search();
      } else {
        setPage(0);
      }
      setSubmitting(false);
    }, 500);
  }, [search, page]);

  const handleSearchChange = useCallback((event: ChangeEvent<{ value: unknown }>) => {
    setSearchFilter(event.target.value as string);
  }, []);

  return {
    analysis,
    setAnalysis,
    dataSource: mappedDataSource,
    pagination,
    choices,
    rowsPerPage,
    page,
    searchFilter,
    openDeleteModal,
    openAnalysisProcessingDialog,
    deletingIds,
    selectedIds,
    isLoading,


    handleChangePage,
    handleChangeRowsPerPage,
    handleRowClick,
    handleCloseDeleteModal,
    handleCloseAnalysisProcessingDialog,
    handleOpenAnalysisProcessingModal,
    handleOpenDeleteModal,
    handleSearchSubmit,
    handleSearchChange,
    percentage,
    toggleSelectOne,
    toggleSelectAll,

    DISPLAYED_COLUMNS_SHOW,
    DISPLAYED_COLUMNS,
    SHOW_ACTIONS,
    DATE_COLUMNS,
    STATUS_DOT_COLORS,
    confirmDeleteMessage,
  };
};
